"use client";

import { useEffect, useRef } from "react";
import Link from "next/link";
import AppImage from "@/components/ui/AppImage";

export default function HeroSection() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!heroRef.current) return;

      const scrollY = window.scrollY;
      const heroHeight = heroRef.current.offsetHeight;
      const scrollProgress = Math.min(scrollY / heroHeight, 1);

      // Image scale effect
      const imageWrapper = heroRef.current.querySelector('.hero-image-wrapper') as HTMLElement;
      if (imageWrapper) {
        const scale = 1 + scrollProgress * 0.3;
        imageWrapper.style.transform = `scale(${scale})`;
      }

      // Text fade effect
      const heroText = heroRef.current.querySelector('.hero-text') as HTMLElement;
      if (heroText) {
        heroText.style.opacity = `${1 - scrollProgress * 0.8}`;
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-b from-slate-50 to-white pt-24">

      {/* Background Grid Pattern */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `linear-gradient(to right, #0f172a 1px, transparent 1px), linear-gradient(to bottom, #0f172a 1px, transparent 1px)`,
            backgroundSize: '4rem 4rem'
          }} />

      </div>

      <div className="max-w-7xl mx-auto px-6 w-full relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Text Content */}
          <div className="hero-text space-y-8 transition-opacity duration-300">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 border border-primary/20 rounded-full text-sm font-medium text-primary">
              <span className="status-dot status-active"></span>
              Sanntids flåtesporing
            </div>
            
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter leading-[1.1] text-secondary">
              Spor hvert kjøretøy.<br />
              Hold deg i samsvar.<br />
              <span className="text-primary">Spar tid.</span>
            </h1>
            
            <p className="text-xl text-muted-foreground leading-relaxed max-w-xl">
              Moderne flåtestyring med automatisk registreringssporing, fornyelsesvarslinger og overholdelsesrapportering. Aldri gå glipp av en fornyelsesdato igjen.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                href="/vehicle-registration"
                className="px-8 py-4 bg-primary text-primary-foreground rounded-xl font-semibold hover:bg-primary/90 transition-all shadow-lg hover:shadow-glow text-center">

                Start gratis prøveperiode
              </Link>
              <button className="px-8 py-4 bg-slate-100 text-foreground rounded-xl font-semibold hover:bg-slate-200 transition-all text-center flex items-center justify-center gap-2">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                </svg>
                Se demo
              </button>
            </div>

            {/* Trust Indicators */}
            <div className="flex flex-wrap items-center gap-8 pt-8">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <svg className="w-5 h-5 text-success" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
                </svg>
                SOC 2 sertifisert
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <svg className="w-5 h-5 text-success" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
                </svg>
                GDPR-kompatibel
              </div>
              <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                10 000+ kjøretøy sporet daglig
              </div>
            </div>
          </div>

          {/* Dashboard Preview Image */}
          <div className="relative">
            <div className="hero-image-wrapper transition-transform duration-300 ease-out">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-slate-200">
                <AppImage
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_15cef7977-1767045597959.png"
                  alt="Oslo Bil Auto AS dashbord som viser flåteoversikt med kjøretøyliste, statusindikatorer og fornyelsesvarslinger på moderne grensesnitt"
                  className="w-full h-auto" />

                {/* Overlay gradient */}
                <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent"></div>
              </div>
              
              {/* Floating Stats Card */}
              <div className="absolute -bottom-8 -left-8 bg-white rounded-2xl shadow-xl border border-slate-200 p-6 max-w-xs animate-fade-in">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-success/10 rounded-xl flex items-center justify-center">
                    <svg className="w-6 h-6 text-success" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">98,5%</p>
                    <p className="text-sm text-muted-foreground">Fornyelser i tide</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

}